// Voice client for the voice-gateway WebSocket (/v1/realtime), copied from the
// gateway's reference web client so the protocol matches byte for byte:
//
//   -> {type:"hello", profile}                 on open
//   -> {type:"start"}                          when the hand goes down
//   -> binary frames of 16 kHz mono PCM16      while holding
//   -> {type:"commit"}                         on release (THIS is the send)
//   <- listening / hearing / answering / idle  phase events
//   <- stt.partial / stt.final                 forming + final transcript
//   <- brain.token / brain.final               streamed reply text
//   <- tts.sentence {wav_b64} | tts.chunk {pcm_b64, rate}   spoken audio
//
// Half-duplex: the mic never records while the agent is answering. A screen
// wake lock is held during an active voice session and re-acquired on
// visibilitychange, so the phone does not sleep mid-conversation.

import { VOICE_GATEWAY_URL, VOICE_PROFILE } from "./config";

export type VoicePhase = "idle" | "listening" | "hearing" | "answering";

export interface VoiceEvents {
  onConnected(ok: boolean): void;
  onPhase(phase: VoicePhase): void;
  onPartial(text: string): void;
  onFinal(text: string): void;
  onToken(piece: string): void;
  onReplyDone(): void;
  onError(message: string): void;
}

export class VoiceClient {
  private ws: WebSocket | null = null;
  private captureCtx: AudioContext | null = null;
  private playCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private recording = false;
  private pressed = false;
  private audioReady = false;
  private connected = false;
  private closed = false;
  private reconnectTimer: number | null = null;
  private phase: VoicePhase = "idle";
  private nextStart = 0;
  private liveSources: AudioBufferSourceNode[] = [];
  private wakeLock: WakeLockSentinel | null = null;
  private sessionActive = false;
  private readonly onVisibility = (): void => {
    if (document.visibilityState === "visible" && this.sessionActive) {
      void this.acquireWakeLock();
      void this.flushQueueOnVisible();
    }
  };

  constructor(private readonly events: VoiceEvents) {}

  start(): void {
    this.closed = false;
    document.addEventListener("visibilitychange", this.onVisibility);
    this.connect();
  }

  stop(): void {
    this.closed = true;
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.stopPlayback();
    this.ws?.close();
    this.ws = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.captureCtx?.close().catch(() => {});
    this.captureCtx = null;
    this.playCtx?.close().catch(() => {});
    this.playCtx = null;
    this.audioReady = false;
    this.sessionActive = false;
    void this.releaseWakeLock();
  }

  getPhase(): VoicePhase {
    return this.phase;
  }

  isConnected(): boolean {
    return this.connected;
  }

  // ── websocket ────────────────────────────────────────────────────────────
  private connect(): void {
    const sep = VOICE_GATEWAY_URL.includes("?") ? "&" : "?";
    const url = `${VOICE_GATEWAY_URL}${sep}profile=${encodeURIComponent(VOICE_PROFILE)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.events.onConnected(false);
      return;
    }
    this.ws = ws;
    ws.binaryType = "arraybuffer";
    ws.onopen = () => {
      this.connected = true;
      this.events.onConnected(true);
      ws.send(JSON.stringify({ type: "hello", profile: VOICE_PROFILE }));
    };
    ws.onclose = () => {
      this.connected = false;
      this.recording = false;
      this.pressed = false;
      this.setPhase("idle");
      this.events.onConnected(false);
      if (!this.closed) {
        this.reconnectTimer = window.setTimeout(() => this.connect(), 2000);
      }
    };
    ws.onerror = () => {
      this.connected = false;
      this.events.onConnected(false);
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== "string") return;
      try {
        this.onMessage(JSON.parse(e.data) as Record<string, unknown>);
      } catch {
        /* ignore non-JSON frames */
      }
    };
  }

  private onMessage(m: Record<string, unknown>): void {
    switch (m["type"]) {
      case "ready":
        break;
      case "listening":
        this.setPhase("listening");
        break;
      case "hearing":
        this.setPhase("hearing");
        break;
      case "answering":
        this.setPhase("answering");
        break;
      case "idle":
        // The gateway sends `idle` when synthesis finishes, which can be
        // seconds before the scheduled audio stops playing — and the patient
        // is usually mid-conversation. Keep the session (and the wake lock)
        // alive until the view unmounts via stop(), so the screen never
        // sleeps between turns and visibilitychange re-acquire keeps working.
        this.setPhase("idle");
        break;
      case "stt.partial":
        this.events.onPartial(String(m["text"] ?? ""));
        break;
      case "stt.final":
        this.events.onFinal(String(m["text"] ?? ""));
        break;
      case "brain.token":
        this.events.onToken(String(m["text"] ?? ""));
        break;
      case "brain.final":
        this.events.onReplyDone();
        break;
      case "tts.sentence":
        void this.playSentence(String(m["wav_b64"] ?? ""));
        break;
      case "tts.chunk":
        this.playChunk(String(m["pcm_b64"] ?? ""), Number(m["rate"] ?? 24000));
        break;
      case "error":
        this.events.onError(String(m["message"] ?? "voice error"));
        break;
      default:
        break;
    }
  }

  private setPhase(p: VoicePhase): void {
    this.phase = p;
    this.events.onPhase(p);
  }

  // ── push-to-talk ─────────────────────────────────────────────────────────
  async pressTalk(): Promise<void> {
    // Half-duplex: the gateway only accepts `start` from rest — a press while
    // it is still hearing or answering would be silently discarded server-side,
    // so refuse it here instead of eating the utterance.
    if (this.phase !== "idle" || this.recording) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // Mark the press synchronously: initAudio can block for seconds on the
    // getUserMedia permission prompt, and a release during that wait must not
    // be lost (it would leave a hot mic streaming with no commit to end it).
    this.pressed = true;
    try {
      await this.initAudio();
    } catch {
      this.pressed = false;
      this.events.onError("mic");
      return;
    }
    // Released (or cancelled) while we were waiting for the mic? Abort: never
    // send `start` for a press that has already ended. Also bail if another
    // overlapping press already started recording — one `start` per turn.
    if (!this.pressed || this.recording) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.recording = true;
    this.sessionActive = true;
    this.ws.send(JSON.stringify({ type: "start" }));
    await this.acquireWakeLock();
  }

  releaseTalk(): void {
    this.pressed = false;
    if (!this.recording) return;
    this.recording = false;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "commit" }));
    }
  }

  cancel(): void {
    this.pressed = false;
    this.recording = false;
    this.stopPlayback();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "cancel" }));
    }
  }

  // ── mic capture (16 kHz mono PCM16 via AudioWorklet) ─────────────────────
  private async initAudio(): Promise<void> {
    if (this.audioReady) {
      // iOS suspends contexts on backgrounding; resume before use.
      if (this.captureCtx?.state === "suspended") await this.captureCtx.resume();
      if (this.playCtx?.state === "suspended") await this.playCtx.resume();
      return;
    }
    this.captureCtx = new AudioContext({ sampleRate: 16000 });
    await this.captureCtx.audioWorklet.addModule("/pcm-worklet.js");
    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    const src = this.captureCtx.createMediaStreamSource(this.micStream);
    this.workletNode = new AudioWorkletNode(this.captureCtx, "pcm-worklet");
    this.workletNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      if (this.recording && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(e.data);
      }
    };
    src.connect(this.workletNode);
    // never connect the worklet to the destination — we must not hear the mic
    this.playCtx = new AudioContext();
    this.audioReady = true;
  }

  // ── playback (ordered, gapless, half-duplex) ─────────────────────────────
  private async playSentence(wavB64: string): Promise<void> {
    if (!wavB64) return;
    if (!this.playCtx) this.playCtx = new AudioContext();
    if (this.playCtx.state === "suspended") await this.playCtx.resume();
    const bytes = Uint8Array.from(atob(wavB64), (c) => c.charCodeAt(0));
    let audio: AudioBuffer;
    try {
      audio = await this.playCtx.decodeAudioData(bytes.buffer);
    } catch {
      return;
    }
    this.schedule(audio);
  }

  private playChunk(pcmB64: string, rate: number): void {
    if (!pcmB64) return;
    if (!this.playCtx) this.playCtx = new AudioContext();
    const ctx = this.playCtx;
    if (ctx.state === "suspended") void ctx.resume();
    const bytes = Uint8Array.from(atob(pcmB64), (c) => c.charCodeAt(0));
    if (bytes.length < 2) return;
    const pcm = new Int16Array(bytes.buffer, 0, bytes.length >> 1);
    const f32 = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) f32[i] = (pcm[i] ?? 0) / 32768;
    const buf = ctx.createBuffer(1, f32.length, rate || 24000);
    buf.copyToChannel(f32, 0);
    this.schedule(buf);
  }

  private schedule(buffer: AudioBuffer): void {
    const ctx = this.playCtx;
    if (!ctx) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    const at = Math.max(now, this.nextStart);
    src.start(at);
    this.nextStart = at + buffer.duration;
    this.liveSources.push(src);
    src.onended = () => {
      const i = this.liveSources.indexOf(src);
      if (i >= 0) this.liveSources.splice(i, 1);
    };
  }

  private stopPlayback(): void {
    for (const s of this.liveSources.splice(0)) {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    }
    this.nextStart = 0;
  }

  // ── wake lock ────────────────────────────────────────────────────────────
  private async acquireWakeLock(): Promise<void> {
    if (!("wakeLock" in navigator)) return;
    try {
      this.wakeLock = await navigator.wakeLock.request("screen");
      this.wakeLock.addEventListener("release", () => {
        this.wakeLock = null;
      });
    } catch {
      /* denied (low battery / background) — voice still works, screen may dim */
    }
  }

  private async releaseWakeLock(): Promise<void> {
    try {
      await this.wakeLock?.release();
    } catch {
      /* already released */
    }
    this.wakeLock = null;
  }

  private async flushQueueOnVisible(): Promise<void> {
    // Coming back to the foreground mid-session: resume suspended audio so the
    // reply keeps playing where iOS paused it.
    if (this.playCtx?.state === "suspended") await this.playCtx.resume();
    if (this.captureCtx?.state === "suspended") await this.captureCtx.resume();
  }
}
